import { ChevronIcon } from '../../../bombiix-icon';
import type { BombiixCellRendererParams, BombiixRow } from '../types';

/**
 * Deliberate exception to the size={24} icon convention. In-grid expand
 * chevrons are sized to the `.cag-chevron-icon` box so they sit inside the row
 * without growing its height, and this must stay identical to the shared
 * `ExpandChevronCell`, which renders the same affordance at the same size.
 */
const EXPAND_CHEVRON_SIZE = 20;

interface HierExpandExtras {
  isParent?: (row: BombiixRow | undefined) => boolean;
  getExpanded?: (row: BombiixRow | undefined) => boolean;
  onToggle?: (row: BombiixRow | undefined) => void;
}

type HierExpandCellProps = BombiixCellRendererParams & HierExpandExtras;

/**
 * Caller-controlled expand/collapse chevron for flat-hierarchy tables. Renders
 * nothing unless `isParent(row)`; use when the grid drives expansion from its
 * own flat rowData rather than AG-Grid master/detail.
 *
 * Draws the same affordance as the shared `ExpandChevronCell` — the master-design
 * `ChevronIcon` inside `.bombiix-icon-container`, pointing down when collapsed and
 * rotated 180° by `.cag-chevron-icon--expanded` when open. The two differ only in
 * where the expanded state lives, so they must not differ in how they look.
 */
function HierExpandCell(params: HierExpandCellProps) {
  const { data, isParent, getExpanded, onToggle } = params;
  if (typeof isParent === 'function' && !isParent(data)) return null;

  const expanded = typeof getExpanded === 'function' ? getExpanded(data) : false;
  const label = expanded ? 'Collapse row' : 'Expand row';

  return (
    <button type="button" className="bombiix-icon-container" aria-label={label} aria-expanded={expanded} title={label} onClick={() => onToggle?.(data)}>
      <ChevronIcon size={EXPAND_CHEVRON_SIZE} className={`cag-chevron-icon${expanded ? ' cag-chevron-icon--expanded' : ''}`} />
    </button>
  );
}

export default HierExpandCell;
